/**
 * Visit Notes Controller (Driver Adapter)
 *
 * Interface layer between UI and business logic
 */

import type {
  IVisitNotesController,
  IVisitNotesService,
  ICreateVisitNoteRequest,
  IUpdateVisitNoteRequest,
  ICreateVisitNoteResponse,
  IUpdateVisitNoteResponse,
  IGetAllVisitNotesResponse,
  IGetVisitNoteProvidersResponse,
  ISummarizeNarrativeRequest,
  ISummarizeNarrativeResponse,
  ISendEfaxRequest,
  ISendEfaxResponse,
  IGetSidePeekProfileResponse,
  IGenerateSidePeekProfileResponse,
  IUpdateSidePeekProfileResponse,
  IGetSafetyNoteResponse,
  IUpdateSafetyNoteResponse,
  IGetCodeStatusResponse,
  IUpdateCodeStatusResponse,
  CodeStatusValue,
  IGetPatientContactsResponse,
  IUpdatePatientContactsResponse,
  IUpdatePatientContactsRequest,
  IGetNextVisitPlanResponse,
  IUpdateNextVisitPlanResponse,
  INextVisitPlan,
  IGetPatientSummaryResponse,
  IGeneratePatientSummaryResponse,
  IGetNoteLlmOutputResponse,
  IClearManualEditsHistoryResponse,
} from "../../core/ports/driver/VisitNotesService";

export const VisitNotesControllerFactory = ({
  visitNotesService,
}: {
  visitNotesService: IVisitNotesService;
}): IVisitNotesController => {
  /**
   * Create a new visit note (draft or completed)
   */
  const createVisitNote = async (
    request: ICreateVisitNoteRequest
  ): Promise<ICreateVisitNoteResponse> => {
    return await visitNotesService.createVisitNote(request);
  };

  /**
   * Update/auto-save a visit note
   */
  const updateVisitNote = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IUpdateVisitNoteRequest
  ): Promise<IUpdateVisitNoteResponse> => {
    return await visitNotesService.updateVisitNote(
      clinicId,
      patientId,
      noteId,
      request
    );
  };

  /**
   * Get all visit notes for a clinic
   */
  const getAllVisitNotes = async (
    clinicId: number,
    params?: {
      monthOffset?: number;
      daysBack?: number;
      daysForward?: number;
      providerEmail?: string;
      providerName?: string;
      includeEmpty?: boolean;
      skipHealthscribe?: boolean;
    }
  ): Promise<IGetAllVisitNotesResponse> => {
    return await visitNotesService.getAllVisitNotes(clinicId, params);
  };

  /**
   * Get distinct providers for the visit notes provider filter dropdown
   */
  const getVisitNoteProviders = async (
    clinicId: number
  ): Promise<IGetVisitNoteProvidersResponse> => {
    return await visitNotesService.getVisitNoteProviders(clinicId);
  };

  /**
   * Server-side search for visit notes (transcript search only).
   * Empty query returns all third_party_np_visit notes in date range.
   */
  const searchVisitNotes = async (
    clinicId: number,
    searchQuery?: string,
    limit?: number
  ): Promise<IGetAllVisitNotesResponse> => {
    return await visitNotesService.searchVisitNotes(
      clinicId,
      searchQuery,
      limit
    );
  };

  /**
   * Get archived visit notes for a clinic
   */
  const getArchivedVisitNotes = async (
    clinicId: number
  ): Promise<IGetAllVisitNotesResponse> => {
    return await visitNotesService.getArchivedVisitNotes(clinicId);
  };

  /**
   * Restore an archived visit note
   */
  const restoreArchivedVisitNote = async (
    clinicId: number,
    ipvNoteId: number
  ) => {
    return await visitNotesService.restoreArchivedVisitNote(
      clinicId,
      ipvNoteId
    );
  };

  /**
   * Get a single visit note
   */
  const getVisitNote = async (
    noteId: number
  ): Promise<{ success: boolean; data?: any; error?: any }> => {
    return await visitNotesService.getVisitNote(noteId);
  };

  /**
   * Summarize a narrative for sharing as PDF
   */
  const summarizeNarrative = async (
    request: ISummarizeNarrativeRequest
  ): Promise<ISummarizeNarrativeResponse> => {
    return await visitNotesService.summarizeNarrative(request);
  };

  /**
   * Send visit note PDF via eFax
   */
  const sendEfax = async (
    request: ISendEfaxRequest
  ): Promise<ISendEfaxResponse> => {
    return await visitNotesService.sendEfax(request);
  };

  /**
   * Get Side Peek Profile for a patient
   */
  const getSidePeekProfile = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSidePeekProfileResponse> => {
    return await visitNotesService.getSidePeekProfile(clinicId, patientId);
  };

  /**
   * Trigger Side Peek Profile generation for a patient
   */
  const generateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    section?: "diagnoses" | "medications" | "allergies",
    model?: string
  ): Promise<IGenerateSidePeekProfileResponse> => {
    return await visitNotesService.generateSidePeekProfile(
      clinicId,
      patientId,
      noteId,
      section,
      model
    );
  };

  /**
   * Update Side Peek Profile for a patient (manual edits)
   */
  const updateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    updateData: {
      diagnoses?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        icd10_code?: string | null;
        is_primary?: boolean | null;
        onset_date?: string | null;
      }>;
      medications?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        status?: string | null;
      }>;
      allergies?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        icd10_code?: string | null;
        status?: string | null;
        allergen?: string | null;
        reaction?: string | null;
        severity?: string | null;
        onset_date?: string | null;
        is_primary?: boolean | null;
      }>;
    }
  ): Promise<IUpdateSidePeekProfileResponse> => {
    return await visitNotesService.updateSidePeekProfile(
      clinicId,
      patientId,
      updateData
    );
  };

  /**
   * Get Safety Note for a patient
   */
  const getSafetyNote = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSafetyNoteResponse> => {
    return await visitNotesService.getSafetyNote(clinicId, patientId);
  };

  /**
   * Update Safety Note for a patient
   */
  const updateSafetyNote = async (
    clinicId: number,
    patientId: number,
    content: string
  ): Promise<IUpdateSafetyNoteResponse> => {
    return await visitNotesService.updateSafetyNote(
      clinicId,
      patientId,
      content
    );
  };

  /**
   * Get Code Status for a patient
   */
  const getCodeStatus = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetCodeStatusResponse> => {
    return await visitNotesService.getCodeStatus(clinicId, patientId);
  };

  /**
   * Update Code Status for a patient
   */
  const updateCodeStatus = async (
    clinicId: number,
    patientId: number,
    status: CodeStatusValue,
    otherStatus?: string | null
  ): Promise<IUpdateCodeStatusResponse> => {
    return await visitNotesService.updateCodeStatus(
      clinicId,
      patientId,
      status,
      otherStatus
    );
  };

  /**
   * Get Patient Contacts
   */
  const getPatientContacts = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientContactsResponse> => {
    return await visitNotesService.getPatientContacts(clinicId, patientId);
  };

  /**
   * Add or remove a patient contact
   */
  const updatePatientContacts = async (
    clinicId: number,
    patientId: number,
    request: IUpdatePatientContactsRequest
  ): Promise<IUpdatePatientContactsResponse> => {
    return await visitNotesService.updatePatientContacts(
      clinicId,
      patientId,
      request
    );
  };

  /**
   * Get Next Visit Plan for a patient
   */
  const getNextVisitPlan = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetNextVisitPlanResponse> => {
    return await visitNotesService.getNextVisitPlan(clinicId, patientId);
  };

  /**
   * Update Next Visit Plan for a patient
   */
  const updateNextVisitPlan = async (
    clinicId: number,
    patientId: number,
    plan: INextVisitPlan["plan"]
  ): Promise<IUpdateNextVisitPlanResponse> => {
    return await visitNotesService.updateNextVisitPlan(
      clinicId,
      patientId,
      plan
    );
  };

  const getPatientSummary = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientSummaryResponse> => {
    return await visitNotesService.getPatientSummary(clinicId, patientId);
  };

  const generatePatientSummary = async (
    clinicId: number,
    patientId: number
  ): Promise<IGeneratePatientSummaryResponse> => {
    return await visitNotesService.generatePatientSummary(clinicId, patientId);
  };

  /**
   * Get latest LLM output for a note
   */
  const getNoteLlmOutput = async (
    noteId: number,
    clinicId: number
  ): Promise<IGetNoteLlmOutputResponse> => {
    return await visitNotesService.getNoteLlmOutput(noteId, clinicId);
  };

  /**
   * Clear manual edits history for a note
   */
  const clearManualEditsHistory = async (
    noteId: number,
    clinicId: number
  ): Promise<IClearManualEditsHistoryResponse> => {
    return await visitNotesService.clearManualEditsHistory(noteId, clinicId);
  };

  return {
    createVisitNote,
    updateVisitNote,
    getAllVisitNotes,
    getVisitNoteProviders,
    searchVisitNotes,
    getArchivedVisitNotes,
    restoreArchivedVisitNote,
    getVisitNote,
    summarizeNarrative,
    sendEfax,
    getSidePeekProfile,
    generateSidePeekProfile,
    updateSidePeekProfile,
    getSafetyNote,
    updateSafetyNote,
    getCodeStatus,
    updateCodeStatus,
    getPatientContacts,
    updatePatientContacts,
    getNextVisitPlan,
    updateNextVisitPlan,
    getPatientSummary,
    generatePatientSummary,
    getNoteLlmOutput,
    clearManualEditsHistory,
  };
};

export type IVisitNotesControllerType = ReturnType<
  typeof VisitNotesControllerFactory
>;
